/* eslint-disable no-console */
import { useQuery } from '@tanstack/react-query';
import useNotifications from '~/context/Notifications';
import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';
import { GetAutoReportsSchema } from '~/views/Dashboard/AutomatedReporting/automatedReporting.utils';

type RawReportFormData = Omit<GetAutoReportsSchema, 'toEmail'> & { toEmail: string[] };

type ResponseData = RawReportFormData[] | ErrorObjectString;

export async function getAutomatedReports(isPartner?: boolean, id?: string | number) {
  if (isPartner === undefined || id === undefined) {
    throw new Error('Session data is undefined');
  }
  const accountType = isPartner ? 'partner' : 'client';

  const requestOptions = {
    headers: {
      'content-header': 'application/JSON',
    },
    useNodeEndpoint: true,
  };

  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/${accountType}/${id}/reports/scheduled`,
    requestOptions,
  );

  if ('error' in data) {
    throw new Error(data.error);
  }

  const newData = data.map((val) => {
    return {
      ...val,
      toEmail: val.toEmail.map((value) => {
        return {
          user: value,
        };
      }),
      appendTotalRow: !!val.appendTotalRow,
    };
  });

  return newData;
}

export function getAutomatedReportsQueryOptions(isPartner?: boolean, accountId?: string | number) {
  return {
    queryKey: ['useAutomatedReportQuery', isPartner, accountId] as const,
    queryFn: () => getAutomatedReports(isPartner, accountId),
    refetchOnWindowFocus: false,
  };
}

function useAutomatedReportQuery(isPartner?: boolean, accountId?: string | number) {
  const { setTimedNotification } = useNotifications();
  return useQuery({
    ...getAutomatedReportsQueryOptions(isPartner, accountId),
    queryFn: () =>
      getAutomatedReports(isPartner, accountId).catch((error) => {
        if (
          error instanceof Error &&
          error.cause instanceof Response &&
          error.cause.status === 403
        ) {
          setTimedNotification(
            'danger',
            'Looks like your account does not have permissions for this feature. Please contact your account manager to enable this.',
            5000,
          );
          throw error;
        }

        setTimedNotification(
          'danger',
          'There has been an error loading data. Please reload and try again',
          5000,
        );
        throw error;
      }),
    enabled: accountId !== undefined && isPartner !== undefined,
  });
}

export default useAutomatedReportQuery;
